/* eslint-disable no-console */
import {
  FilterUnion,
  IDestinationEntity,
  IMigrationDestination,
  IPracticeMigration,
  ISourceEntityRecord,
  ISourceEntityRecordFilter,
  ISourceEntityTypes,
  SourceEntityRecordStatus,
  SourceEntityStatus,
  type ISourceEntity,
  ISourceHandler,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  SourceEntityRecord_Key,
  TimestampString,
  UUIDString,
  getLastUpdatedSourceEntityRecords,
  getSourceEntityRecordCount,
  getSourceEntityRecordsByUid,
  resetAllMigrationDestinationsByType,
  searchSourceEntityRecords,
  searchSourceEntityRecordsByFilter,
  searchSourceEntityRecordsByMigrationDestination,
} from '@principle-theorem/principle-db-connector';
import {
  SystemActors,
  WithPrimaryKey,
  getEnumValues,
  toMoment,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { DataConnect } from 'firebase/data-connect';
import { first } from 'lodash';
import { PracticeMigration } from '../../practice-migrations';

export class SourceEntity {
  static init(overrides: Pick<ISourceEntity, 'metadata'>): ISourceEntity {
    return {
      uid: overrides.metadata.idPrefix,
      status: SourceEntityStatus.Uninitialised,
      ...overrides,
    };
  }

  static async records<
    Source extends ISourceEntityTypes,
    FilterValue = string,
  >(data: {
    migration: WithRef<IPracticeMigration>;
    sourceEntity: WithRef<ISourceEntity>;
    statuses: SourceEntityRecordStatus[];
    migrationStatus?: IMigrationDestination;
    filter?: ISourceEntityRecordFilter<FilterValue>;
    limit: number;
    offset?: number;
  }): Promise<WithPrimaryKey<ISourceEntityRecord<Source>>[]> {
    try {
      const results = await SourceEntity.buildSourceQuery<FilterValue>(data);
      return results.map((result) => this.unserialise(result));
    } catch (error) {
      console.error('SourceEntity.records', error);
      throw error;
    }
  }

  static async buildSourceQuery<FilterValue = string>(data: {
    migration: WithRef<IPracticeMigration>;
    sourceEntity: WithRef<ISourceEntity>;
    statuses: SourceEntityRecordStatus[];
    migrationStatus?: IMigrationDestination | undefined;
    filter?: ISourceEntityRecordFilter<FilterValue> | undefined;
    limit: number;
    offset?: number | undefined;
  }): Promise<SourceEntityHandlerResponse[]> {
    const dataConnect = await PracticeMigration.dataConnect(data.migration);
    const props = {
      sourceType: data.sourceEntity.metadata.idPrefix,
      statuses: data.statuses,
      limit: data.limit,
      offset: data.offset,
    };

    if (data.filter) {
      return (
        await searchSourceEntityRecordsByFilter(dataConnect, {
          ...props,
          filter: data.filter,
        })
      ).data.sourceEntityRecords;
    }

    if (data.migrationStatus) {
      return (
        await searchSourceEntityRecordsByMigrationDestination(dataConnect, {
          ...props,
          migrationDestinationKey: data.migrationStatus.key,
          migrationDestinationStatus: data.migrationStatus.value,
        })
      ).data.sourceEntityRecords;
    }

    return (await searchSourceEntityRecords(dataConnect, props)).data
      .sourceEntityRecords;
  }

  static async getSourceRecordEntityByUid<Source extends ISourceEntityTypes>(
    migration: WithRef<IPracticeMigration>,
    sourceEntity: WithRef<ISourceEntity>,
    uid: string
  ): Promise<WithPrimaryKey<ISourceEntityRecord<Source>> | undefined> {
    try {
      const dataConnect = await PracticeMigration.dataConnect(migration);
      const results = await getSourceEntityRecordsByUid(dataConnect, {
        sourceType: sourceEntity.metadata.idPrefix,
        uid,
      });

      const result = first(results.data.sourceEntityRecords);
      if (!result) {
        return;
      }

      return this.unserialise(result);
    } catch (error) {
      console.error('SourceEntity.getSourceRecordEntityByUid', error);
      throw error;
    }
  }

  static async getLastSourceRecordEntity<Source extends ISourceEntityTypes>(
    migration: WithRef<IPracticeMigration>,
    sourceEntity: WithRef<ISourceEntity>
  ): Promise<WithPrimaryKey<ISourceEntityRecord<Source>> | undefined> {
    try {
      const dataConnect = await PracticeMigration.dataConnect(migration);
      const results = await getLastUpdatedSourceEntityRecords(dataConnect, {
        sourceType: sourceEntity.metadata.idPrefix,
      });

      const result = first(results.data.sourceEntityRecords);
      if (!result) {
        return;
      }

      return this.unserialise(result);
    } catch (error) {
      console.error('SourceEntity.getLastSourceRecordEntity', error);
      throw error;
    }
  }

  static async getSourceRecordEntityCount(
    dataConnect: DataConnect,
    sourceEntity: WithRef<ISourceEntity>,
    statuses: SourceEntityRecordStatus[] = getEnumValues(
      SourceEntityRecordStatus
    )
  ): Promise<number> {
    try {
      console.log(
        `Fetching source entity ${
          sourceEntity.metadata.idPrefix
        } record count with statuses: ${statuses.join(', ')}`
      );
      const results = await getSourceEntityRecordCount(dataConnect, {
        sourceType: sourceEntity.metadata.idPrefix,
        statuses,
      });
      console.log(
        `Source Entity ${sourceEntity.metadata.idPrefix} record count: ${results.data.sourceEntityRecords.length} `
      );
      return results.data.sourceEntityRecords.length;
    } catch (error) {
      console.error('SourceEntity.getSourceRecordEntityCount', error);
      throw error;
    }
  }

  static async resetAllMigrationDestinationsByType(
    migration: WithRef<IPracticeMigration>,
    sourceEntityKey: string,
    destinationKey: string
  ): Promise<void> {
    try {
      await resetAllMigrationDestinationsByType(
        await PracticeMigration.dataConnect(migration),
        {
          sourceType: sourceEntityKey,
          destinationKey,
        }
      );
    } catch (error) {
      console.error('SourceEntity.resetAllMigrationDestinationsByType', error);
      throw error;
    }
  }

  static async resetSourceEntityMigrationStatus(
    migration: WithRef<IPracticeMigration>,
    destinationEntity: WithRef<IDestinationEntity>,
    sourceHandler: ISourceHandler
  ): Promise<void> {
    try {
      const sourceToReset = sourceHandler.entityHandlers.find((entityHandler) =>
        (entityHandler.migrationDestinations ?? []).includes(
          destinationEntity.metadata.key
        )
      );

      if (!sourceToReset) {
        console.error(`Couldn't find source to reset`);
        return;
      }

      await SourceEntity.resetAllMigrationDestinationsByType(
        migration,
        sourceToReset.sourceEntity.metadata.idPrefix,
        destinationEntity.metadata.key
      );
    } catch (error) {
      console.error('SourceEntity.resetSourceEntityMigrationStatus', error);
      throw error;
    }
  }

  static determineUidForRecord(
    rawDataId: string | number,
    sourceEntity: ISourceEntity
  ): string {
    return [sourceEntity.metadata.idPrefix, String(rawDataId)].join('-');
  }

  static unserialise<Source extends ISourceEntityTypes>(
    record: SourceEntityHandlerResponse
  ): WithPrimaryKey<ISourceEntityRecord> {
    return {
      ...record,
      filters: record.filters as FilterUnion<Source['filters']>[],
      translations: record.translations as Source['translations'],
      migrationDestinations:
        record.migrationDestinations as IMigrationDestination[],
      data: record.data as Source['data'],
      modifiedData: record.modifiedData as Source['data'],
      status: record.status as SourceEntityRecordStatus,
      lastSync: toTimestamp(toMoment(record.lastSync)),
      createdAt: toTimestamp(toMoment(record.createdAt)),
      createdBy: record.createdBy as SystemActors,
      updatedAt: toTimestamp(toMoment(record.updatedAt)),
      updatedBy: record.updatedBy as SystemActors,
    };
  }
}

export type SourceEntityHandlerResponse = {
  id: UUIDString;
  sourceType: string;
  uid: string;
  label: string;
  lastSync: TimestampString;
  status: string;
  filters: unknown[];
  translations: unknown;
  migrationDestinations: unknown[];
  data: unknown;
  modifiedData?: unknown;
  createdAt: TimestampString;
  createdBy?: string | null;
  updatedAt: TimestampString;
  updatedBy?: string | null;
} & SourceEntityRecord_Key;
